/**
 * Zero-cost Arabic morphology engine for Al-Diwan.
 * Rule-based only — no AI, no paid APIs.
 *
 * Design goals:
 *   1. generateStemCandidates  — fuzzy matching: surface form → set of possible stems
 *   2. detectVerbForm          — identify verb form I–X from consonant skeleton
 *   3. generateVerbParadigm   — past / present / imperative / active participle / passive participle
 *   4. soundMasculinePlural / soundFemininePlural — regular plural suffixes
 *   5. brokenPluralCandidates — ranked list of the 12 most common broken-plural patterns
 *
 * Limitations:
 *   - Works on consonant skeletons (after stripping harakat). Cannot distinguish
 *     Form I (فَعَلَ) from Form II (فَعَّلَ) or Form IX (اِفعَلَّ) without vowels.
 *   - Weak verbs (roots with و / ي / ء) undergo letter elision and vowel shifts
 *     that pure template expansion cannot reproduce correctly.
 *   - Broken plural predictions are probabilistic; real Arabic has ~30 patterns
 *     and the "right" one is lexically determined, not rule-predictable.
 *   - Verb form detection (detectVerbForm) is reliable for Forms IV / VII / VIII / X
 *     (unique prefixes) but ambiguous for I / II / III without vowels.
 *
 * Usage:
 *   import { generateStemCandidates, generateVerbParadigm } from '../lib/arabic-morphology';
 */

// ── Shared helpers ────────────────────────────────────────────────────────────

/** Remove all tashkeel (harakat, shadda, sukun, tatweel). */
export function stripHarakat(w = '') {
  return w.replace(/[ً-ٰٟـ]/g, '');
}

/**
 * Normalise a surface form for matching.
 *   بِالطَلَبِ → طلب  |  الطَلَبُ → طلب  |  وَالبُهتانَ → بهتان
 * Strips: harakat, one حرف prefix (وفبلك) before ال, standalone ال.
 */
export function normalizeArabicWord(w = '') {
  let s = stripHarakat(w);
  if (/^[وفبلك]ال/.test(s)) s = s.slice(1);
  if (s.startsWith('ال')) s = s.slice(2);
  return s;
}

// ── Stem candidate generation ─────────────────────────────────────────────────

// مضارع personal prefixes (present tense markers).
// ا covers أَ (first person singular, hamza normalised to alif after strip).
const MUDHARE_PREFIXES = new Set(['ي', 'ت', 'ن', 'ا']);

// Verb suffix strip list — longest first to avoid partial matches.
// Covers: past-tense personal endings + common attached pronoun clitics.
const VERB_SUFFIXES = [
  'تموهن', 'تموها', 'تموهم', 'تمونا', 'تموني',
  'وهن', 'وها', 'وهم', 'ونا', 'وني',
  'تنّ', 'تما',
  'تم', 'تن',
  'ون', 'ين', 'ان', 'نا', 'وا',
  'هما', 'هم', 'هن',
  'ني', 'ها', 'ه',
  'ك',
  'ت', 'ا',
];

/**
 * Generate candidate stems from a surface form for fuzzy vocab matching.
 *
 * Returns 2–12 normalised strings, each a plausible consonant skeleton.
 * The caller should try each candidate against the vocab until a match is found.
 *
 * Handles:
 *   يَكتُبُونَ → ['يكتبون', 'كتبون', 'يكتب', 'كتب']   (مضارع prefix + suffix)
 *   كَتَبتُم  → ['كتبتم', 'كتب']                       (past-tense suffix)
 *   بِالطَلَبِ → ['طلب']                                (ال prefix — already handled by normalizeArabicWord)
 *
 * Does NOT handle:
 *   - Active/passive participles (كَاتِب، مَكتُوب) — different consonant skeleton
 *   - Weak verb elisions (قال → root ق-و-ل, but strip gives قال ≠ قول)
 *   - Broken plurals (أَسباب → root س-ب-ب)
 */
export function generateStemCandidates(word) {
  const norm = normalizeArabicWord(word);
  const out = new Set([norm]);

  function tryDeprefixed(s) {
    if (s.length >= 4 && MUDHARE_PREFIXES.has(s[0])) {
      const dep = s.slice(1);
      if (dep.length >= 3) out.add(dep);
      return dep;
    }
    return null;
  }

  // 1. Strip مضارع prefix
  const dep = tryDeprefixed(norm);

  // 2. Strip verb suffixes from norm and from deprefixed form
  for (const s of [norm, dep].filter(Boolean)) {
    for (const sfx of VERB_SUFFIXES) {
      if (s.endsWith(sfx) && s.length - sfx.length >= 3) {
        const stem = s.slice(0, -sfx.length);
        out.add(stem);
        // Also deprefixed version of suffix-stripped form (يَكتُبُونَ → كتبون → كتب)
        tryDeprefixed(stem);
      }
    }
  }

  return [...out];
}

// ── Verb form detection ───────────────────────────────────────────────────────

/**
 * Detect verb form (I–X) from a surface form (works best with harakat; without
 * them, Forms I/II/III may be misidentified — see limitations above).
 *
 * Returns 'I' | 'II' | 'III' | 'IV' | 'V' | 'VI' | 'VII' | 'VIII' | 'IX' | 'X' | null
 */
export function detectVerbForm(surface) {
  const s = stripHarakat(surface);
  if (/^است/.test(s))                              return 'X';
  if (/^ان[^ا]/.test(s))                          return 'VII';
  if (/^ا.ت[^ا]/.test(s))                         return 'VIII';
  if (/^[أا][^اوي]/.test(s) && s.length <= 5)    return 'IV';
  if (/^تفا/.test(s))                             return 'VI';
  if (/^ت[^ا]/.test(s) && s.length >= 5)         return 'V';
  if (/^[^أايوتن].ا./.test(s))                   return 'III';
  return 'I'; // I / II / IX are indistinguishable without vowels
}

// ── Verb paradigm generation ──────────────────────────────────────────────────

// Consonant-skeleton templates for each form.
// R = [R1, R2, R3] — the three root radicals (after stripping harakat).
// All outputs are unvowelled consonant skeletons; real forms need full harakat.
const FORM_SKELETONS = {
  I:    { past: r => `${r[0]}${r[1]}${r[2]}`,             present: r => `${r[0]}${r[1]}${r[2]}`,          imp: r => `${r[0]}${r[1]}${r[2]}`,           ap: r => `${r[0]}ا${r[1]}${r[2]}`,         pp: r => `م${r[0]}${r[1]}و${r[2]}`         },
  II:   { past: r => `${r[0]}${r[1]}${r[1]}${r[2]}`,      present: r => `${r[0]}${r[1]}${r[1]}${r[2]}`,   imp: r => `${r[0]}${r[1]}${r[1]}${r[2]}`,    ap: r => `م${r[0]}${r[1]}${r[1]}${r[2]}`,  pp: r => `م${r[0]}${r[1]}${r[1]}${r[2]}`   },
  III:  { past: r => `${r[0]}ا${r[1]}${r[2]}`,            present: r => `${r[0]}ا${r[1]}${r[2]}`,         imp: r => `${r[0]}ا${r[1]}${r[2]}`,          ap: r => `م${r[0]}ا${r[1]}${r[2]}`,        pp: r => `م${r[0]}ا${r[1]}${r[2]}`         },
  IV:   { past: r => `ا${r[0]}${r[1]}${r[2]}`,            present: r => `${r[0]}${r[1]}${r[2]}`,          imp: r => `ا${r[0]}${r[1]}${r[2]}`,          ap: r => `م${r[0]}${r[1]}${r[2]}`,         pp: r => `م${r[0]}${r[1]}${r[2]}`          },
  V:    { past: r => `ت${r[0]}${r[1]}${r[1]}${r[2]}`,     present: r => `ت${r[0]}${r[1]}${r[1]}${r[2]}`,  imp: r => `ت${r[0]}${r[1]}${r[1]}${r[2]}`,   ap: r => `مت${r[0]}${r[1]}${r[1]}${r[2]}`, pp: r => `مت${r[0]}${r[1]}${r[1]}${r[2]}`  },
  VI:   { past: r => `ت${r[0]}ا${r[1]}${r[2]}`,           present: r => `ت${r[0]}ا${r[1]}${r[2]}`,        imp: r => `ت${r[0]}ا${r[1]}${r[2]}`,         ap: r => `مت${r[0]}ا${r[1]}${r[2]}`,       pp: r => `مت${r[0]}ا${r[1]}${r[2]}`        },
  VII:  { past: r => `ان${r[0]}${r[1]}${r[2]}`,           present: r => `ن${r[0]}${r[1]}${r[2]}`,         imp: r => `ان${r[0]}${r[1]}${r[2]}`,         ap: r => `من${r[0]}${r[1]}${r[2]}`,        pp: null                                    },
  VIII: { past: r => `ا${r[0]}ت${r[1]}${r[2]}`,           present: r => `${r[0]}ت${r[1]}${r[2]}`,         imp: r => `ا${r[0]}ت${r[1]}${r[2]}`,         ap: r => `م${r[0]}ت${r[1]}${r[2]}`,        pp: r => `م${r[0]}ت${r[1]}${r[2]}`         },
  X:    { past: r => `است${r[0]}${r[1]}${r[2]}`,          present: r => `ست${r[0]}${r[1]}${r[2]}`,        imp: r => `است${r[0]}${r[1]}${r[2]}`,        ap: r => `مست${r[0]}${r[1]}${r[2]}`,       pp: r => `مست${r[0]}${r[1]}${r[2]}`        },
};

/**
 * Generate verb paradigm consonant skeletons from a 3-letter root array.
 *
 * Example:
 *   generateVerbParadigm(['ك','ت','ب'], 'I')
 *   → { past:'كتب', present:'كتب', imperative:'كتب', activePart:'كاتب', passivePart:'مكتوب' }
 *
 * Note: output is unvowelled. Adding correct harakat requires a lexicon lookup.
 */
export function generateVerbParadigm(root3, form = 'I') {
  if (!root3 || root3.length !== 3) return null;
  const t = FORM_SKELETONS[form];
  if (!t) return null;
  return {
    past:        t.past(root3),
    present:     t.present(root3),
    imperative:  t.imp(root3),
    activePart:  t.ap(root3),
    passivePart: t.pp ? t.pp(root3) : null,
  };
}

// ── Noun plurals ──────────────────────────────────────────────────────────────

/** Sound masculine plural suffixes (nominative / accusative-genitive). */
export function soundMasculinePlural(stem) {
  const s = stripHarakat(stem);
  return { nom: `${s}ون`, acc: `${s}ين` };
}

/** Sound feminine plural suffix — strips ة before adding ات. */
export function soundFemininePlural(stem) {
  const s = stripHarakat(stem);
  return `${s.endsWith('ة') ? s.slice(0, -1) : s}ات`;
}

/**
 * Generate broken-plural consonant skeleton candidates from a 3-letter root.
 * Returns an ordered array — most common patterns first.
 * Flag as ambiguous when the array has more than one entry (almost always).
 *
 * Covers 12 of the ~30 patterns attested in classical Arabic.
 * The correct form is lexically determined; these are educated guesses.
 */
export function brokenPluralCandidates(root3) {
  if (!root3 || root3.length !== 3) return [];
  const [r1, r2, r3] = root3;
  return [
    `ا${r1}${r2}ا${r3}`,      // أَفعَال  (e.g. أَسبَاب — most frequent in classical)
    `${r1}${r2}و${r3}`,        // فُعُول   (e.g. كُتُوب)
    `${r1}${r2}ا${r3}`,        // فِعَال   (e.g. رِجَال)
    `${r1}${r2}${r3}اء`,       // فُعَلاء  (e.g. شُعَرَاء — فعيل adjectives)
    `${r1}${r2}${r3}ة`,        // فَعَلة   (e.g. كَتَبَة)
    `ا${r1}${r2}${r3}`,        // أَفعُل   (e.g. أَنهُر)
    `${r1}ُ${r2}${r3}`,        // فُعُل    (e.g. كُتُب)
    `${r1}${r2}ائ${r3}`,       // فَعائِل  (e.g. رَسَائِل — from رِسَالة)
    `${r1}و${r2}ا${r3}`,       // فَوَاعِل (e.g. قَوَافِل)
    `م${r1}ا${r2}${r3}`,       // مَفَاعِل (e.g. مَسَاجِد — from مَسجِد)
    `${r1}${r2}${r3}ان`,       // فَعلَان
    `${r1}و${r2}${r3}`,        // فَوعَل
  ];
}
